// CommandConsole.jsx
// Hamburger menu modal with 3 tabbed sections
// Replaces the Command sidebar with a cleaner overlay

import React, { useState } from 'react';
import { useWorldState } from './state/WorldStateContext';
import { getAvailableAirports } from '../systems/AirportDatabase.js';
import { calculateTier } from '../systems/ReputationEngine.js';
import {
  X,
  Shield,
  Users,
  Megaphone,
  Handshake,
  UserCircle,
  Network,
  Radio,
  Paintbrush,
  Cpu,
  Wrench,
} from 'lucide-react';

// Tab definitions
const TABS = [
  { id: 'intel', label: 'INTEL', icon: Shield },
  { id: 'personnel', label: 'PERSONNEL', icon: Users },
  { id: 'marketing', label: 'MARKETING', icon: Megaphone },
];

export function CommandConsole({ isOpen, onClose, onOpenPanel }) {
  const [activeTab, setActiveTab] = useState('intel');
  const { worldState } = useWorldState();

  // Extract data for panel stats
  const relations = worldState?.relations || null;
  const cash = worldState?.airline?.cash || 0;
  const tier = calculateTier(worldState?.reputation?.currentScore || 0);
  const crewInMarket = worldState?.crewMarket?.available?.length || 12;

  // Calculate available negotiations
  const availableNegotiations = getAvailableNegotiations(relations, tier);

  // Get active routes count
  const flights = worldState?.flights || [];
  const routeSet = new Set(flights.map(f => `${f.origin}-${f.destination}`));
  const activeRoutesCount = routeSet.size;

  // Current ad spend
  const currentAdSpend = worldState?.advertising?.monthlySpend || 0;

  if (!isOpen) return null;

  const handlePanelClick = (panelName) => {
    onOpenPanel(panelName);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 100,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '480px',
          maxHeight: '80vh',
          backgroundColor: '#0a0a0a',
          border: '1px solid #2a2a2a',
          borderRadius: '12px',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #1a1a1a',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '2px',
              color: '#60ffd0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '10px' }}>◆</span>
            COMMAND CONSOLE
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Bar */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #1a1a1a',
          }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: isActive ? '#151515' : 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #60ffd0' : '2px solid transparent',
                  color: isActive ? '#60ffd0' : '#666',
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.15s ease',
                }}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
          }}
        >
          {activeTab === 'intel' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ConsoleCard
                icon={Handshake}
                title="NEGOTIATIONS"
                subtitle={`${availableNegotiations.length} airports accepting bids`}
                onClick={() => handlePanelClick('negotiations')}
                disabled={availableNegotiations.length === 0}
                disabledReason="No airports in range"
              />
              <ConsoleCard
                icon={UserCircle}
                title="INNER CIRCLE"
                subtitle="Strategic specialists for hub bid intel"
                onClick={() => handlePanelClick('innerCircle')}
              />
              <ConsoleCard
                icon={Cpu}
                title="TECH SYSTEMS"
                subtitle="CrewWise, WebLogic, YieldMaster"
                onClick={() => handlePanelClick('tech')}
                disabled={tier < 2}
                disabledReason="Tier 2+ required"
              />
            </div>
          )}

          {activeTab === 'personnel' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ConsoleCard
                icon={Users}
                title="HIRE CONSULTANTS"
                subtitle="Negotiators, Lawyers, Fixers, Analysts"
                onClick={() => handlePanelClick('consultants')}
              />
              <ConsoleCard
                icon={Users}
                title="HIRE CREW"
                subtitle={`${crewInMarket} pilots in market`}
                onClick={() => handlePanelClick('crew')}
              />
              <ConsoleCard
                icon={Wrench}
                title="SUPPORT CREW"
                subtitle="Mechanics, Engineers, Fleet Ops"
                onClick={() => handlePanelClick('support')}
              />
            </div>
          )}

          {activeTab === 'marketing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ConsoleCard
                icon={Network}
                title="ROUTE NETWORK"
                subtitle={`${activeRoutesCount} active routes`}
                onClick={() => handlePanelClick('routes')}
              />
              <ConsoleCard
                icon={Radio}
                title="ADVERTISING"
                subtitle={`Current spend: $${formatMoney(currentAdSpend)}/mo`}
                onClick={() => handlePanelClick('advertising')}
              />
              <ConsoleCard
                icon={Paintbrush}
                title="REBRANDING"
                subtitle="Reset relations - $2M"
                onClick={() => handlePanelClick('rebrand')}
                disabled={cash < 2000000}
                disabledReason="Insufficient funds"
                danger
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Individual command card
function ConsoleCard({ icon: Icon, title, subtitle, onClick, disabled, disabledReason, danger }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: hovered && !disabled ? '#151515' : '#111',
        border: `1px solid ${danger ? '#661111' : hovered && !disabled ? '#333' : '#222'}`,
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          backgroundColor: '#0a0a0a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={20} style={{ color: danger ? '#ff4444' : '#60ffd0' }} />
      </div>

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            color: '#fff',
            marginBottom: '4px',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: disabled ? '#ff4444' : '#666',
          }}
        >
          {disabled ? disabledReason : subtitle}
        </div>
      </div>

      {!disabled && (
        <div
          style={{
            color: hovered ? '#60ffd0' : '#444',
            transition: 'color 0.15s ease',
          }}
        >
          &gt;
        </div>
      )}
    </div>
  );
}

// Helper functions
function getAvailableNegotiations(relations, tier) {
  const availableAirports = getAvailableAirports(tier || 1);
  return availableAirports
    .map((a) => a.code)
    .filter((code) => {
      if (!relations?.airports) return true;
      const airport = relations.airports?.[code];
      if (!airport) return true;
      const playerRelation = airport.loyalty?.PLAYER || 0;
      return playerRelation > -50;
    });
}

function formatMoney(cents) {
  if (cents >= 100000000) {
    return `${(cents / 100000000).toFixed(1)}M`;
  } else if (cents >= 100000) {
    return `${(cents / 100000).toFixed(0)}K`;
  }
  return cents.toLocaleString();
}

export default CommandConsole;
